import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice, getImageUrl } from '../utils/helpers';
import API from '../api/axios';
import toast from 'react-hot-toast';

const CheckoutPage = () => {
  const { cartItems, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    street: user?.address?.street || '',
    city: user?.address?.city || '',
    state: user?.address?.state || '',
    zipCode: user?.address?.zipCode || '',
    paymentMethod: 'cod',
    notes: '',
  });

  const shipping = subtotal >= 5000 ? 0 : 300;
  const total = subtotal + shipping;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) return toast.error('Your cart is empty');
    if (!form.name || !form.phone || !form.street || !form.city) {
      return toast.error('Please fill all required fields');
    }

    setLoading(true);
    try {
      const orderData = {
        items: cartItems.map((i) => ({
          product: i._id,
          name: i.name,
          image: i.images?.[0],
          price: i.price,
          quantity: i.qty,
        })),
        shippingAddress: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          street: form.street,
          city: form.city,
          state: form.state,
          zipCode: form.zipCode,
        },
        paymentMethod: form.paymentMethod,
        subtotal,
        shippingCost: shipping,
        tax: 0,
        total,
        notes: form.notes,
      };

      const { data } = await API.post('/orders', orderData);
      clearCart();
      toast.success('Order placed successfully!');
      navigate(`/order-success/${data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error placing order');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="container-custom py-20 text-center">
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Your cart is empty</h2>
        <Link to="/shop" className="btn-primary mt-4 inline-block">Browse Products</Link>
      </div>
    );
  }

  return (
    <div className="container-custom py-8">
      <h1 className="section-title mb-8">Checkout</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card p-6">
              <h2 className="font-bold text-lg mb-5">Shipping Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Full Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} required className="input-field" placeholder="Enter full name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Phone *</label>
                  <input name="phone" value={form.phone} onChange={handleChange} required className="input-field" placeholder="e.g. 03001234567" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} className="input-field" placeholder="email@example.com" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">City *</label>
                  <input name="city" value={form.city} onChange={handleChange} required className="input-field" placeholder="e.g. Lahore" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Street Address *</label>
                  <input name="street" value={form.street} onChange={handleChange} required className="input-field" placeholder="House no., street, area" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">State / Province</label>
                  <input name="state" value={form.state} onChange={handleChange} className="input-field" placeholder="e.g. Punjab" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Postal Code</label>
                  <input name="zipCode" value={form.zipCode} onChange={handleChange} className="input-field" placeholder="e.g. 54000" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <h2 className="font-bold text-lg mb-5">Payment Method</h2>
              <div className="space-y-3">
                {[
                  { value: 'cod', label: 'Cash on Delivery (COD)', desc: 'Pay when your order arrives' },
                  { value: 'bank_transfer', label: 'Bank Transfer', desc: 'Pay via bank transfer after placing order' },
                ].map(({ value, label, desc }) => (
                  <label key={value} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${form.paymentMethod === value ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="paymentMethod" value={value} checked={form.paymentMethod === value} onChange={handleChange} className="mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{label}</p>
                      <p className="text-xs text-gray-500">{desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="card p-6">
              <h2 className="font-bold text-lg mb-4">Order Notes (Optional)</h2>
              <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} className="input-field" placeholder="Any special instructions for your order..." />
            </div>
          </div>

          {/* Order summary */}
          <div>
            <div className="card p-6 sticky top-24">
              <h2 className="font-bold text-lg mb-5">Order Summary</h2>
              <div className="space-y-3 mb-5 max-h-64 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item._id} className="flex items-center gap-3">
                    <img src={getImageUrl(item.images?.[0])} alt={item.name} className="w-12 h-12 object-contain rounded-lg bg-gray-50" onError={(e) => { e.target.src = '/placeholder.png'; }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.qty}</p>
                    </div>
                    <span className="text-sm font-semibold text-primary-600">{formatPrice(item.price * item.qty)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span><span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? <span className="text-green-600 font-medium">FREE</span> : formatPrice(shipping)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-100">
                  <span>Total</span>
                  <span className="text-primary-600">{formatPrice(total)}</span>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full mt-5 py-3 text-base flex items-center justify-center">
                {loading ? <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CheckoutPage;
